import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import API_BASE_URL from "../../../config/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { FaBullhorn, FaFileAlt, FaFolderOpen, FaCheckCircle, FaClock, FaCalendarAlt, FaUserTie } from "react-icons/fa";

function TAHomeContainer() {

    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [announcements, setAnnouncements] = useState([]);
    const [recentProjects, setRecentProjects] = useState([]);
    const [stats, setStats] = useState({
        totalProjects: 0,
        activeProjects: 0,
        pendingApprovals: 0,
    });
    const [loading, setLoading] = useState(true);

    const gotoReport = () => {
        navigate("/thrustarea/report");
    };

    const gotoAnnouncement = () => {
        navigate("/thrustarea/announcements");
    };

    const viewProject = (projectId) => {
        navigate(`/project/${projectId}`);
    };

    useEffect(() => {
        setName(user?.name || "Thrust Area Coordinator");

        const fetchData = async () => {
            try {
                if (!isAuthenticated) {
                    setLoading(false);
                    return;
                }

                await Promise.all([fetchAnnouncements(), fetchProjects()]);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
                setLoading(false);
            }
        };

        fetchData();
        AOS.init({ duration: 1000 });
    }, [user, isAuthenticated, navigate, user?.coordinatorFor]);

    const fetchAnnouncements = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/projects/announcements/general`
            );

            if (response.status !== 200) {
                throw new Error("Failed to fetch announcements");
            }

            setAnnouncements(response.data);
        } catch (error) {
            console.error("Error fetching announcements:", error);
            if (error.response?.status === 401) {
                navigate("/");
            }
        }
    };

    const fetchProjects = async () => {
        try {
            const userThrustArea = user?.coordinatorFor;

            if (!userThrustArea) {
                throw new Error("Thrust area not found");
            }

            const response = await axios.get(
                `${API_BASE_URL}/api/projects/thrustarea/${user?.coordinatorFor}`,
                {
                    headers: {
                        Authorization: `Bearer ${user?.token}`,
                    },
                }
            );

            if (response.status !== 200) {
                throw new Error("Failed to fetch projects");
            }

            const data = response.data.data || [];

            const active = data.filter(
                (project) =>
                    project.status === "approved" ||
                    project.status === "guide-assigned"
            ).length;

            const pending = data.filter(
                (project) =>
                    project.status === "rejected" ||
                    project.status === "pending"
            ).length;

            setStats({
                totalProjects: data.length,
                activeProjects: active,
                pendingApprovals: pending,
            });

            const sortedProjects = data
                .sort(
                    (a, b) =>
                        new Date(b.createdAt || b.date) -
                        new Date(a.createdAt || a.date)
                )
                .slice(0, 5);

            setRecentProjects(sortedProjects);
        } catch (error) {
            console.error("Error fetching projects:", error);
            if (error.response?.status === 401) {
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    };

    const renderGuidePreference = (preference) => {
        if (!preference) return "No preference";
        if (typeof preference === "object" && preference.email) {
            return preference.email;
        }
        return preference;
    };

    const StatusBadge = ({ status }) => {
        let colors = "bg-gray-100 text-gray-600 border-gray-200";
        switch (status?.toLowerCase()) {
            case 'approved': colors = "bg-green-100 text-green-700 border-green-200"; break;
            case 'rejected': colors = "bg-red-100 text-red-700 border-red-200"; break;
            case 'pending': colors = "bg-yellow-100 text-yellow-700 border-yellow-200"; break;
            case 'guide-assigned': colors = "bg-blue-100 text-blue-700 border-blue-200"; break;
            case 'needs-modifications': colors = "bg-blue-100 text-blue-700 border-blue-200"; break;
            default: break;
        }
        return (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide border ${colors}`}>
                {status || "Pending"}
            </span>
        );
    };

    return (
        <div className="min-h-screen p-4 md:p-8 font-sans">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Top Section */}
                <div className="bg-[#B7202E] rounded-2xl shadow-lg p-6 md:p-8 text-white flex flex-col md:flex-row justify-between items-start md:items-center gap-6" data-aos="fade-down">
                    <div className="flex-1">
                        <h1 className="text-2xl md:text-3xl font-bold mb-2">Thrust Area Dashboard</h1>
                        <p className="text-white/90 text-lg mb-1">Welcome back, {name}</p>
                        <p className="text-white/70 text-sm font-medium flex items-center gap-2">
                            <FaCalendarAlt />
                            {new Date().toLocaleDateString("en-IN", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-4 w-full md:w-auto">
                        <button
                            className="flex-1 md:flex-none bg-white text-[#B7202E] px-5 py-3 rounded-xl font-bold hover:bg-gray-50 hover:shadow-lg transition-all flex items-center justify-center gap-2 group"
                            onClick={gotoAnnouncement}
                        >
                            <FaBullhorn className="group-hover:scale-110 transition-transform" />
                            Make Announcement
                        </button>
                        <button
                            className="flex-1 md:flex-none bg-[#161dc7] text-white px-5 py-3 rounded-xl font-bold hover:bg-[#1218a8] hover:shadow-lg transition-all flex items-center justify-center gap-2 group"
                            onClick={gotoReport}
                        >
                            <FaFileAlt className="group-hover:scale-110 transition-transform" />
                            Generate Report
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-aos="fade-up">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-5 transition-all hover:-translate-y-1 hover:shadow-md">
                        <div className="w-14 h-14 rounded-xl bg-[#B7202E]/10 flex items-center justify-center text-[#B7202E] text-2xl">
                            <FaFolderOpen />
                        </div>
                        <div>
                            <h3 className="text-3xl font-bold text-gray-800 mb-1">{stats.totalProjects}</h3>
                            <p className="text-gray-500 font-medium text-sm">Total Projects</p>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-5 transition-all hover:-translate-y-1 hover:shadow-md">
                        <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center text-green-600 text-2xl">
                            <FaCheckCircle />
                        </div>
                        <div>
                            <h3 className="text-3xl font-bold text-gray-800 mb-1">{stats.activeProjects}</h3>
                            <p className="text-gray-500 font-medium text-sm">Active Projects</p>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-5 transition-all hover:-translate-y-1 hover:shadow-md">
                        <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 text-2xl">
                            <FaClock />
                        </div>
                        <div>
                            <h3 className="text-3xl font-bold text-gray-800 mb-1">{stats.pendingApprovals}</h3>
                            <p className="text-gray-500 font-medium text-sm">Pending Approvals</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Recent Projects */}
                    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 flex flex-col" data-aos="fade-up">
                        <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2 pb-4 border-b border-gray-100">
                            Recent Projects
                        </h2>

                        <div className="flex-1 relative">
                            {/* Vertical Line */}
                            <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gray-100"></div>

                            {loading ? (
                                <div className="text-center py-12 text-gray-400">Loading projects...</div>
                            ) : recentProjects.length > 0 ? (
                                <div className="space-y-8">
                                    {recentProjects.map((project, index) => (
                                        <div key={index} className="relative pl-8 group">
                                            {/* Dot */}
                                            <div className="absolute left-0 top-1.5 w-4 h-4 rounded-full bg-[#B7202E] border-4 border-white shadow-sm z-10"></div>

                                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <h3 className="text-base font-bold text-gray-800 mb-1 group-hover:text-[#B7202E] transition-colors">{project.projectTitle || project.title}</h3>
                                                    <div className="flex items-center gap-3 text-sm mb-2">
                                                        <StatusBadge status={project.status} />
                                                        <span className="text-gray-400 text-xs">{new Date(project.createdAt || project.date).toLocaleDateString("en-IN", {
                                                            year: "numeric",
                                                            month: "numeric",
                                                            day: "numeric",
                                                        })}</span>
                                                    </div>
                                                    <div className="text-sm text-gray-500 flex items-center gap-1.5">
                                                        <FaUserTie className="text-gray-400" />
                                                        <span className="font-medium">Guide Pref:</span> {renderGuidePreference(project.guide?.preference1)}
                                                    </div>
                                                </div>
                                                <button
                                                    className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium bg-[#B7202E] text-white transition-colors border border-gray-200 hover:border-[#B7202E]"
                                                    onClick={() => viewProject(project._id || project.id)}
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-400 italic">No recent project activity</div>
                            )}
                        </div>
                    </div>

                    {/* Announcements */}
                    <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 flex flex-col h-full">
                        <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2 pb-4 border-b border-gray-100">
                            Announcements
                        </h2>

                        <div className="space-y-4 overflow-y-auto max-h-[600px] pr-2 custom-scrollbar">
                            {announcements.length > 0 ? (
                                announcements.map((announcement, index) => (
                                    <div key={index} className="bg-gray-50 p-4 rounded-xl border border-gray-100 hover:shadow-sm transition-all">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="font-bold text-[#B7202E] text-sm">{announcement.title}</h3>
                                            <span className="text-[10px] uppercase font-semibold text-gray-400 bg-white px-2 py-1 rounded border border-gray-100">
                                                {new Date(announcement.date).toLocaleDateString("en-IN", {
                                                    year: "numeric",
                                                    month: "numeric",
                                                    day: "numeric",
                                                })}
                                            </span>
                                        </div>
                                        <p className="text-gray-600 text-sm leading-relaxed">{announcement.message}</p>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-12 text-gray-400 italic bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                    No announcements available
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TAHomeContainer;
